// src/components/MonitoringProject/ScheduleSettings/ScheduleSettings.tsx
import { useState, useEffect } from 'react';
import { useMonitoringProjects } from '../../../hooks/useMonitoringProjects';
import type { ProjectSchedule } from '../../../api/types';
import styles from './ScheduleSettings.module.css';
import { FiClock, FiSave, FiInfo } from 'react-icons/fi';

import { logger } from '../../../utils/logger';

interface ScheduleSettingsProps {
  projectId: string;
  onSave?: () => void;
}

export function ScheduleSettings({ projectId, onSave }: ScheduleSettingsProps) {
  const { getSchedule, updateSchedule, isGettingSchedule, isUpdatingSchedule } = useMonitoringProjects();
  
  const [tierBased, setTierBased] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [frequencies, setFrequencies] = useState({
    default: 'daily' as 'hourly' | 'daily' | '3days' | 'weekly',
    tier_a: 'daily' as 'hourly' | 'daily' | '3days' | 'weekly',
    tier_b: '3days' as 'hourly' | 'daily' | '3days' | 'weekly',
    tier_c: 'weekly' as 'hourly' | 'daily' | '3days' | 'weekly',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load current schedule on mount
  useEffect(() => {
    const loadSchedule = async () => {
      try {
        setLoading(true);
        const schedule = await getSchedule(projectId);
        
        setEnabled(schedule.enabled);
        setTierBased(schedule.tier_based || false);
        setFrequencies({
          default: schedule.frequency,
          tier_a: schedule.tier_frequencies?.tier_a_frequency || 'daily',
          tier_b: schedule.tier_frequencies?.tier_b_frequency || '3days',
          tier_c: schedule.tier_frequencies?.tier_c_frequency || 'weekly',
        });
      } catch (err: any) {
        logger.error('Failed to load schedule:', err);
        setError(err.message || 'Failed to load schedule');
      } finally {
        setLoading(false);
      }
    };

    loadSchedule();
  }, [projectId]);

  const handleSave = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      const scheduleUpdate: Partial<ProjectSchedule> = {
        frequency: frequencies.default,
        tier_based: tierBased,
        enabled,
      };

      if (tierBased) {
        scheduleUpdate.tier_frequencies = {
          tier_a_frequency: frequencies.tier_a,
          tier_b_frequency: frequencies.tier_b,
          tier_c_frequency: frequencies.tier_c,
        };
      }

      await updateSchedule(projectId, scheduleUpdate);
      setSuccessMessage('Schedule updated successfully!');
      onSave?.();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      logger.error('Failed to update schedule:', err);
      setError(err.message || 'Failed to update schedule');
    }
  };

  if (loading || isGettingSchedule) {
    return (
      <div className={styles.loading}>
        <FiClock className={styles.spinIcon} size={24} />
        <span>Loading schedule...</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <FiClock size={20} />
          <h3>Crawl Schedule</h3>
        </div>
        <p className={styles.description}>
          Configure how frequently your website is crawled for changes
        </p>
      </div>

      {error && (
        <div className={styles.errorAlert}>
          <span className={styles.alertIcon}>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className={styles.successAlert}>
          <span className={styles.alertIcon}>✅</span>
          <span>{successMessage}</span>
        </div>
      )}

      <div className={styles.section}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className={styles.checkbox}
          />
          <span>Enable scheduled crawling</span>
        </label>
      </div>

      <div className={styles.section}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={tierBased}
            onChange={(e) => setTierBased(e.target.checked)}
            className={styles.checkbox}
          />
          <span>Enable tier-based scheduling</span>
        </label>
        <div className={styles.infoBox}>
          <FiInfo size={14} />
          <span>
            Tier-based scheduling crawls high-priority pages (Tier A) more frequently than low-priority pages (Tier C)
          </span>
        </div>
      </div>

      {!tierBased ? (
        <div className={styles.section}>
          <label className={styles.label}>Crawl Frequency</label>
          <select
            value={frequencies.default}
            onChange={(e) => setFrequencies({
              ...frequencies,
              default: e.target.value as any
            })}
            className={styles.select}
            disabled={!enabled}
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="3days">Every 3 Days</option>
            <option value="weekly">Weekly</option>
          </select>
          <p className={styles.hint}>All URLs will be crawled at this frequency</p>
        </div>
      ) : (
        <div className={styles.tierSection}>
          <div className={styles.tierInfo}>
            <h4>Tier-Based Frequencies</h4>
            <p>Set different crawl frequencies for each importance tier</p>
          </div>

          <div className={styles.tierGrid}>
            <div className={styles.tierItem}>
              <label className={styles.tierLabel}>
                <span className={styles.tierBadge} data-tier="A">Tier A</span>
                <span className={styles.tierDescription}>Top 10% - High Priority</span>
              </label>
              <select
                value={frequencies.tier_a}
                onChange={(e) => setFrequencies({
                  ...frequencies,
                  tier_a: e.target.value as any
                })}
                className={styles.select}
                disabled={!enabled}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="3days">Every 3 Days</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <div className={styles.tierItem}>
              <label className={styles.tierLabel}>
                <span className={styles.tierBadge} data-tier="B">Tier B</span>
                <span className={styles.tierDescription}>Next 30% - Medium Priority</span>
              </label>
              <select
                value={frequencies.tier_b}
                onChange={(e) => setFrequencies({
                  ...frequencies,
                  tier_b: e.target.value as any
                })}
                className={styles.select}
                disabled={!enabled}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="3days">Every 3 Days</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <div className={styles.tierItem}>
              <label className={styles.tierLabel}>
                <span className={styles.tierBadge} data-tier="C">Tier C</span>
                <span className={styles.tierDescription}>Remaining 60% - Low Priority</span>
              </label>
              <select
                value={frequencies.tier_c}
                onChange={(e) => setFrequencies({
                  ...frequencies,
                  tier_c: e.target.value as any
                })}
                className={styles.select}
                disabled={!enabled}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="3days">Every 3 Days</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <button
          onClick={handleSave}
          disabled={isUpdatingSchedule || !enabled}
          className={styles.saveButton}
        >
          <FiSave size={16} />
          {isUpdatingSchedule ? 'Saving...' : 'Save Schedule'}
        </button>
      </div>
    </div>
  );
}

